/** Stable category ids — score tooltip legends are keyed by these in {@link ARAS_SCORE_LEGEND_LINES}. */
export type ArasScoreRowId =
  | "medical-complexity"
  | "therapy-needs"
  | "functional-tolerance"
  | "baseline-change"
  | "diagnosis";

/** One line in the score tooltip (bold number + em dash + text). Omit scores you do not show (e.g. therapy skips 2). */
export type ArasScoreLegendLine = {
  value: 1 | 2 | 3;
  text: string;
};

/**
 * Hardcoded score tooltip copy for each ARAS row. Edit this object only — layout is shared.
 * Rows with an `id` not listed here still use the default medical-flavored score tooltip.
 */
export const ARAS_SCORE_LEGEND_LINES: Record<
  ArasScoreRowId,
  readonly ArasScoreLegendLine[]
> = {
  "medical-complexity": [
    {
      value: 1,
      text: "Stable conditions; routine care, no daily physician oversight needed",
    },
    {
      value: 2,
      text: "Requires regular monitoring; no daily physician intervention needed",
    },
    {
      value: 3,
      text: "Multiple severe/complex conditions requiring daily physician oversight",
    },
  ],
  "therapy-needs": [
    { value: 1, text: "Fewer than 2 therapy disciplines ordered" },
    {
      value: 3,
      text: "2+ therapy disciplines ordered (PT, OT, and/or Speech)",
    },
  ],
  "functional-tolerance": [
    {
      value: 1,
      text: "Unable or significantly limited in completing therapy due to physical, cognitive, or emotional barriers",
    },
    { value: 2, text: "Moderate tolerance; some challenges, outcome unclear" },
    { value: 3, text: "Strong tolerance with minimal issues" },
  ],
  "baseline-change": [
    {
      value: 1,
      text: "Minimal/no deviation; functioning near pre-admission level",
    },
    { value: 2, text: "Moderate decline; partial loss of independence" },
    { value: 3, text: "Significant decline; major loss of independence" },
  ],
  diagnosis: [
    { value: 1, text: "No qualifying diagnosis found" },
    { value: 2, text: "Uncertain/partial match" },
    { value: 3, text: "Clear match to a qualifying diagnosis" },
  ],
};

export function isArasScoreRowId(id: string): id is ArasScoreRowId {
  return Object.prototype.hasOwnProperty.call(ARAS_SCORE_LEGEND_LINES, id);
}
